import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  deletePermissionProfile,
  getPermissionProfiles,
} from '../services/api';
import PageHeader from './common/PageHeader';
import ResponsiveTable from './common/ResponsiveTable';
import EntityFormDialog from './common/EntityFormDialog';
import PermissionProfileForm from './PermissionProfileForm';
import SortableTh from './common/SortableTh';
import { useClientSort } from '../hooks/useClientSort';
import { getApiErrorMessage } from '../utils/apiError';

export default function PermissionProfileList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ open: false, id: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPermissionProfiles();
      setItems(response.data || []);
      setError('');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível carregar os grupos.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const profileAccessors = useMemo(() => ({
    grupo: (p) => p.descricao,
    descontoMaximo: (p) => Number(p.percentualDesconto) || 0,
  }), []);
  const { sorted, orderBy, order, requestSort } = useClientSort(
    items,
    'descricao',
    'asc',
    profileAccessors,
  );

  const abrirForm = (id = null) => setForm({ open: true, id });
  const fecharForm = () => setForm((f) => ({ ...f, open: false }));
  const aoSalvar = (msg) => {
    fecharForm();
    setSnackbar({ open: true, message: msg });
    load();
  };

  const remove = async (profile) => {
    if (!window.confirm(`Excluir o grupo “${profile.descricao}”?`)) return;
    try {
      await deletePermissionProfile(profile.id);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Não foi possível excluir o grupo de permissões.'));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Grupos de permissões"
        subtitle="Perfis compartilhados pelos usuários, no mesmo modelo do ZenPDV."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => abrirForm()}
          >
            Novo grupo
          </Button>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 1 }} />}
      <ResponsiveTable>
        <Table>
          <TableHead>
            <TableRow>
              <SortableTh id="grupo" label="Grupo" orderBy={orderBy} order={order} onSort={requestSort} />
              <SortableTh id="descontoMaximo" label="Desconto máximo" align="center" orderBy={orderBy} order={order} onSort={requestSort} />
              <TableCell align="right"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhum grupo cadastrado
                </TableCell>
              </TableRow>
            )}
            {sorted.map((profile) => (
              <TableRow
                key={profile.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => abrirForm(profile.id)}
              >
                <TableCell data-label="Grupo">
                  <Typography fontWeight={800}>{profile.descricao}</Typography>
                </TableCell>
                <TableCell align="center" data-label="Desconto máximo">
                  {Number(profile.percentualDesconto || 0).toLocaleString('pt-BR')}%
                </TableCell>
                <TableCell align="right" data-actions="" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    color="primary"
                    onClick={() => abrirForm(profile.id)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => remove(profile)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ResponsiveTable>

      <EntityFormDialog
        open={form.open}
        title={form.id ? 'Editar grupo de permissões' : 'Novo grupo de permissões'}
        subtitle={form.id ? `Código ${form.id}` : null}
        onClose={fecharForm}
        maxWidth="lg"
        fullHeight
      >
        <PermissionProfileForm
          key={form.id || 'novo'}
          embedded
          profileId={form.id}
          onCancel={fecharForm}
          onSaved={aoSalvar}
        />
      </EntityFormDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
